import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import { TableRow, TableCell } from '@mui/material';

import { FHSkeletonLoader } from '../../base';
import { metricsActions } from '../../../store';
import { formatSecondsToHHMM2 } from '../../../helpers';
import { API_DATE_FORMAT } from '../../../constants';
import { NOT_AVAILABLE, SupervisionMetricsPeriod } from '../../../pages/constants';
import { FORTA_BLACK, FORTA_ERROR } from '../../../utils/colors';

interface SingleClientViewRowProps {
  id: string;
  startDate: string;
  endDate: string;
  period: string;
}

const SingleClientViewRow = ({ id, startDate, endDate, period }: SingleClientViewRowProps) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [failed, setFailed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState({
    snHours: 0,
    csnHours: 0,
    ratio: 0,
    psnHours: 0,
    caHours: 0,
    gpsnHours: 0,
  });

  useEffect(() => {
    async function fetchAggregates() {
      setLoading(true);
      const response = await dispatch(
        metricsActions.getSupervisionMetrics({
          getAccessTokenSilently,
          start: moment(startDate).format(API_DATE_FORMAT),
          end: moment(endDate).format(API_DATE_FORMAT),
          clientId: id,
        }),
      );
      if (response.error) {
        setFailed(true);
      } else {
        setFailed(false);
        const {
          client_supervision_duration,
          csn_session_ratio,
          parent_supervision_duration,
          session_duration,
          client_assessment_duration,
          group_parent_supervision_duration,
        } = response.payload[0] ?? {};
        const data = {
          snHours: session_duration ?? 0,
          csnHours: client_supervision_duration ?? 0,
          ratio: csn_session_ratio,
          psnHours: parent_supervision_duration ?? 0,
          caHours: client_assessment_duration ?? 0,
          gpsnHours: group_parent_supervision_duration ?? 0,
        };
        setData(data);
      }
      setLoading(false);
    }
    fetchAggregates();
  }, []);

  const csnSnRatioVal = data.ratio === null ? NOT_AVAILABLE : Number((data.ratio * 100).toFixed(1));
  const dateLabel =
    period === SupervisionMetricsPeriod.Month
      ? `${moment(startDate).format('MMM YY')}`
      : `${moment(startDate).format('MMM DD')} - ${moment(endDate).format('MMM DD')}`;

  return (
    <TableRow>
      <TableCell sx={{ color: failed ? FORTA_ERROR : FORTA_BLACK }}>
        {loading ? <FHSkeletonLoader width={100} /> : dateLabel}
      </TableCell>
      <TableCell sx={{ color: failed ? FORTA_ERROR : FORTA_BLACK }}>
        {loading ? (
          <FHSkeletonLoader width={100} />
        ) : failed ? (
          'Error'
        ) : (
          formatSecondsToHHMM2(data.snHours)
        )}
      </TableCell>
      <TableCell sx={{ color: failed ? FORTA_ERROR : FORTA_BLACK }}>
        {loading ? (
          <FHSkeletonLoader width={100} containerStyles={{ p: 0.75 }} />
        ) : failed ? (
          'Error'
        ) : (
          formatSecondsToHHMM2(data.csnHours)
        )}
      </TableCell>
      <TableCell
        sx={{
          color:
            failed || (csnSnRatioVal !== NOT_AVAILABLE && Number(csnSnRatioVal) < 5)
              ? FORTA_ERROR
              : FORTA_BLACK,
        }}
      >
        {loading ? (
          <FHSkeletonLoader width={100} />
        ) : failed ? (
          'Error'
        ) : csnSnRatioVal === NOT_AVAILABLE ? (
          csnSnRatioVal
        ) : (
          `${csnSnRatioVal}%`
        )}
      </TableCell>
      <TableCell sx={{ color: failed ? FORTA_ERROR : FORTA_BLACK }}>
        {loading ? (
          <FHSkeletonLoader width={100} />
        ) : failed ? (
          'Error'
        ) : (
          formatSecondsToHHMM2(data.psnHours)
        )}
      </TableCell>
      <TableCell sx={{ color: failed ? FORTA_ERROR : FORTA_BLACK }}>
        {loading ? (
          <FHSkeletonLoader width={100} />
        ) : failed ? (
          'Error'
        ) : (
          formatSecondsToHHMM2(data.caHours)
        )}
      </TableCell>
      <TableCell sx={{ color: failed ? FORTA_ERROR : FORTA_BLACK }}>
        {loading ? (
          <FHSkeletonLoader width={100} />
        ) : failed ? (
          'Error'
        ) : (
          formatSecondsToHHMM2(data.gpsnHours)
        )}
      </TableCell>
    </TableRow>
  );
};

export default SingleClientViewRow;
